// js/ui/header.js
// SINGLE-SOURCE marketing site header — one header for every public page (index/start/help) so the
// brand reads continuous across the marketing surface.
//
// Mount point (per page):  <div id="siteHeader" [data-variant="dark"]></div>
//   default        — navy logo + dark nav, for pages on a LIGHT hero (index/start).
//   variant="dark" — white logo + light nav, for pages on a DARK hero (help).
//
// POST WEB-FLIP scope: the authed app is the React client at /app, so the header's old "app" skin
// (avatar menu, prices pill, Add-account, sign-out via window.NS_AUTH, unified footer) is gone with
// the authed HTML pages it served. What remains is the marketing bar: logo + marketing nav + Log in /
// Get started, both pointing at the app (/app). No user data is interpolated, so there is no auth
// dependency and nothing to escape.

(function () {
  // Same glyph in two fills so it reads on either hero: navy on light, white on dark.
  const STAR_DARK = '<svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 2 L18.8 13.2 L30 16 L18.8 18.8 L16 30 L13.2 18.8 L2 16 L13.2 13.2 Z" fill="#0f1e45"/><circle cx="16" cy="16" r="2.8" fill="#fff"/></svg>';
  const STAR_LIGHT = '<svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M16 2 L18.8 13.2 L30 16 L18.8 18.8 L16 30 L13.2 18.8 L2 16 L13.2 13.2 Z" fill="#fff"/><circle cx="16" cy="16" r="2.8" fill="rgba(15,30,69,0.5)"/></svg>';

  // ── Language selector (marketing pages have no account, so this is how a visitor picks a language) ──
  // Each locale ships a real per-URL page (SEO): `/` = English, `/de/`, `/es/`, `/fr/`, `/eu/`. The
  // menu is <details>-based so it needs NO inline script (CSP: script-src 'self'). Basque has no Unicode
  // country flag → a compact inline ikurriña SVG (aria-hidden; the endonym label carries the meaning).
  const IKURRINA = '<svg class="shFlagSvg" viewBox="0 0 30 20" aria-hidden="true"><rect width="30" height="20" fill="#d52b1e"/><path d="M0 0 30 20M30 0 0 20" stroke="#009543" stroke-width="4"/><path d="M15 0V20M0 10H30" stroke="#fff" stroke-width="4"/></svg>';
  const LOCALES = [
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', label: 'Español', flag: '🇪🇸' },
    { code: 'fr', label: 'Français', flag: '🇫🇷' },
    { code: 'eu', label: 'Euskara', flag: IKURRINA },
  ];
  const PREFIXED = ['de', 'es', 'fr', 'eu'];
  // Pages that exist per-locale (SSG-generated). Others (e.g. /start.html, client-rendered) fall back to
  // the locale HOME so a language switch never lands on a 404.
  const LOCALIZED_PAGES = new Set(['/', '/index.html', '/help.html']);

  function currentLocale() {
    const seg = location.pathname.split('/')[1];
    return PREFIXED.includes(seg) ? seg : 'en';
  }
  // The current page path with any leading locale segment stripped (`/de/help.html` → `/help.html`).
  function basePath() {
    const parts = location.pathname.split('/');
    if (PREFIXED.includes(parts[1])) parts.splice(1, 1);
    return parts.join('/') || '/';
  }
  function localeHref(code) {
    const base = basePath();
    const target = LOCALIZED_PAGES.has(base) ? base : '/'; // non-localized page → locale home
    if (code === 'en') return target;                      // English is served at the root
    return '/' + code + (target === '/' ? '/' : target);
  }
  function flagFor(code) {
    const l = LOCALES.find((x) => x.code === code);
    return l ? l.flag : '🌐';
  }

  injectStyles();
  renderHeader();

  function renderHeader() {
    const host = document.getElementById('siteHeader');
    if (!host) return;
    host.innerHTML = marketingHeader(host.dataset.variant === 'dark');
    wireLangDismiss(host);
  }

  // Close the language <details> on outside-click / Escape (polish; <details> alone stays open). Module
  // script, so this is CSP-safe — no inline handlers.
  function wireLangDismiss(host) {
    const det = host.querySelector('details.shLang');
    if (!det) return;
    document.addEventListener('click', (e) => { if (det.open && !det.contains(e.target)) det.open = false; });
    det.addEventListener('keydown', (e) => { if (e.key === 'Escape') { det.open = false; det.querySelector('summary').focus(); } });
  }

  // Transparent over the hero; both CTAs go to the app at /app. `dark` swaps the logo + nav colours
  // for a dark background (help); the purple Get-started button reads on either.
  function marketingHeader(dark) {
    // Intra-marketing .html links stay RELATIVE so they keep the visitor inside the current locale
    // directory (`/de/help.html` → `/de/…`). `start.html` is English-only (client-rendered), so it is
    // ABSOLUTE `/start.html` — a relative `start.html` from `/de/index.html` would 404.
    return `<div class="shBar shBar--marketing${dark ? ' shBar--onDark' : ''}">
        <a href="index.html" class="shLogo">${dark ? STAR_LIGHT : STAR_DARK}<span>Northsee</span></a>
        <nav class="shNav shNav--marketing" aria-label="Primary">
          <a href="index.html#usecases" class="shNavItem">How it works</a>
          <a href="/start.html" class="shNavItem">New to investing</a>
          <a href="help.html" class="shNavItem">Guides</a>
        </nav>
        <div class="shRight">
          ${languageSelector()}
          <a href="/app" class="shMktLogin">Log in</a>
          <a href="/app" class="shMktBtn">Get started</a>
        </div>
      </div>`;
  }

  // Flag + endonym language picker. <details>/<summary> = keyboard-accessible, no inline script (CSP).
  function languageSelector() {
    const cur = currentLocale();
    const items = LOCALES.map((l) => {
      const active = l.code === cur;
      return `<a class="shLangItem${active ? ' is-active' : ''}" href="${localeHref(l.code)}" lang="${l.code}" hreflang="${l.code}"${active ? ' aria-current="true"' : ''}>`
        + `<span class="shFlag" aria-hidden="true">${l.flag}</span><span>${l.label}</span></a>`;
    }).join('');
    return `<details class="shLang">
        <summary aria-label="Change language" title="Change language">
          <span class="shFlag" aria-hidden="true">${flagFor(cur)}</span>
          <svg class="shLangCaret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="shLangMenu" role="menu">${items}</div>
      </details>`;
  }

  // Scoped styles (self-contained: identical on pages that skip the shared styles.css).
  function injectStyles() {
    if (document.getElementById('siteChromeStyles')) return;
    const s = document.createElement('style');
    s.id = 'siteChromeStyles';
    s.textContent = `
      #siteHeader { position: relative; z-index: 10; display: block; }
      .shBar { display: flex; align-items: center; justify-content: space-between; gap: 16px; height: 68px; padding: 0 30px; }
      .shLogo { display: flex; align-items: center; gap: 9px; text-decoration: none; flex-shrink: 0; }
      .shLogo span { font-size: 17px; font-weight: 900; color: var(--ink, #0f1e45); letter-spacing: -.3px; }
      /* Reset: the inner <nav> must not inherit the pages' global nav{} bar styling
         (background/height/border/blur) — that would draw a second block behind the items. */
      .shNav { display: flex; align-items: center; gap: 6px; position: static; height: auto; min-height: 0; margin: 0; padding: 0; background: none; border: 0; box-shadow: none; -webkit-backdrop-filter: none; backdrop-filter: none; }
      .shNavItem { text-decoration: none; font-size: 14px; font-weight: 600; color: var(--muted, #6b7897); padding: 8px 14px; border-radius: 9px; white-space: nowrap; transition: background .12s, color .12s; }
      .shNavItem:hover { color: var(--text, #0f1e45); background: rgba(15,30,69,.05); }
      .shRight { display: flex; align-items: center; gap: 16px; }
      /* Language selector (flag + endonym). <details> dropdown, anchored under the summary. */
      .shLang { position: relative; }
      .shLang summary { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; list-style: none; padding: 7px 9px; border-radius: 9px; color: var(--text, #0f1e45); transition: background .12s; }
      .shLang summary::-webkit-details-marker { display: none; }
      .shLang summary:hover { background: rgba(15,30,69,.05); }
      .shLang summary:focus-visible { outline: 2px solid var(--accent-strong, #5b4fcf); outline-offset: 2px; }
      .shLangCaret { transition: transform .15s; opacity: .7; }
      .shLang[open] summary .shLangCaret { transform: rotate(180deg); }
      .shFlag { font-size: 15px; line-height: 1; display: inline-flex; }
      .shFlagSvg { width: 18px; height: 12px; border-radius: 2px; display: block; }
      .shLangMenu { position: absolute; top: calc(100% + 8px); right: 0; z-index: 30; min-width: 168px; display: flex; flex-direction: column; padding: 6px; background: #fff; border: 1px solid rgba(15,30,69,.1); border-radius: 12px; box-shadow: 0 12px 34px rgba(15,30,69,.16); }
      .shLangItem { display: flex; align-items: center; gap: 10px; padding: 9px 11px; border-radius: 8px; text-decoration: none; font-size: 14px; font-weight: 600; color: var(--text, #0f1e45); white-space: nowrap; transition: background .12s; }
      .shLangItem:hover { background: rgba(108,99,255,.08); }
      .shLangItem.is-active { color: var(--accent-strong, #5b4fcf); background: rgba(108,99,255,.08); }
      /* Dark-hero variant: the summary sits on a dark bg; the menu stays a light popover for legibility. */
      .shBar--onDark .shLang summary { color: rgba(255,255,255,.92); }
      .shBar--onDark .shLang summary:hover { background: rgba(255,255,255,.1); }
      .shMktLogin { text-decoration: none; font-size: 14px; font-weight: 600; color: var(--text, #0f1e45); padding: 8px 12px; white-space: nowrap; }
      .shMktLogin:hover { color: var(--accent-strong, #5b4fcf); }
      /* accent-strong (not accent) as the base so white text clears WCAG AA (6.06:1 vs 4.31:1) on
         every hero — the per-page overrides that used to patch this are now redundant. */
      .shMktBtn { display: inline-flex; align-items: center; gap: 6px; text-decoration: none; font-size: 14px; font-weight: 700; color: #fff; background: var(--accent-strong, #5b4fcf); border-radius: 11px; padding: 9px 17px; box-shadow: 0 6px 16px rgba(108,99,255,.28); white-space: nowrap; transition: filter .12s; }
      .shMktBtn:hover { filter: brightness(0.93); }
      /* Dark-hero variant (help): white logo + light nav, translucent hover. */
      .shBar--onDark .shLogo span { color: #fff; }
      .shBar--onDark .shNavItem { color: rgba(255,255,255,.72); }
      .shBar--onDark .shNavItem:hover { color: #fff; background: rgba(255,255,255,.1); }
      .shBar--onDark .shMktLogin { color: rgba(255,255,255,.92); }
      .shBar--onDark .shMktLogin:hover { color: #fff; }
      @media (max-width: 640px) {
        .shBar { padding: 0 16px; gap: 10px; }
        .shNav--marketing { display: none; }
      }`;
    document.head.appendChild(s);
  }
})();
